import { memo } from 'react'

import AlertTriIcon from '@/assets/icons/alert-tri.svg?react'
import { cn } from '@/utils/style'

interface ResourceUsageCardProps {
  title: string
  // Single value mode (legacy - not recommended)
  used?: number
  // Dual value mode (Physical GPU: vGPU + GPU Passthrough)
  vgpuUsed?: number
  gpuUsed?: number
  // Triple value mode (vCPU, Memory, Disk: by vGPU spec + GPU spec + vCPU spec)
  vgpuSpecUsed?: number
  gpuSpecUsed?: number
  cpuSpecUsed?: number
  total: number
  unit: string
  color?: string // Single value mode color, default bg-active
  isOverLimit?: boolean
}

// Striped error style for over-limit state
const errorStripeStyle = {
  background: `repeating-linear-gradient(
    -55deg,
    #F04438,
    #F04438 4px,
    #F97066 4px,
    #F97066 8px
  )`,
}

export const ResourceUsageCard = memo(function ResourceUsageCard({
  title,
  used,
  vgpuUsed,
  gpuUsed,
  vgpuSpecUsed,
  gpuSpecUsed,
  cpuSpecUsed,
  total,
  unit,
  color = 'bg-active',
  isOverLimit = false,
}: ResourceUsageCardProps) {
  // Determine mode
  const isDualMode = vgpuUsed !== undefined || gpuUsed !== undefined
  const isTripleMode =
    vgpuSpecUsed !== undefined || gpuSpecUsed !== undefined || cpuSpecUsed !== undefined

  // Calculate percentages
  let percentage = 0
  let vgpuPercentage = 0
  let gpuPercentage = 0
  let vgpuSpecPercentage = 0
  let gpuSpecPercentage = 0
  let cpuSpecPercentage = 0
  let remainingPercentage = 100
  let totalUsed = 0

  if (isTripleMode) {
    // Triple mode: vGPU spec (cyan) + GPU spec (blue) + vCPU spec (green)
    const vgpuSpec = vgpuSpecUsed || 0
    const gpuSpec = gpuSpecUsed || 0
    const cpuSpec = cpuSpecUsed || 0
    totalUsed = vgpuSpec + gpuSpec + cpuSpec

    if (total > 0) {
      vgpuSpecPercentage = (vgpuSpec / total) * 100
      gpuSpecPercentage = (gpuSpec / total) * 100
      cpuSpecPercentage = (cpuSpec / total) * 100
      remainingPercentage = Math.max(
        0,
        100 - vgpuSpecPercentage - gpuSpecPercentage - cpuSpecPercentage,
      )
    }
  } else if (isDualMode) {
    // Dual mode: vGPU (cyan) + GPU Passthrough (blue)
    const vgpu = vgpuUsed || 0
    const gpu = gpuUsed || 0
    totalUsed = vgpu + gpu

    if (total > 0) {
      vgpuPercentage = (vgpu / total) * 100
      gpuPercentage = (gpu / total) * 100
      remainingPercentage = Math.max(0, 100 - vgpuPercentage - gpuPercentage)
    }
  } else {
    // Single mode
    totalUsed = used || 0
    if (total > 0) {
      percentage = (totalUsed / total) * 100
    }
  }

  const percentageDisplay = total > 0 ? ((totalUsed / total) * 100).toFixed(2) : '0.00'

  // Render progress bar based on mode
  const renderProgressBar = () => {
    if (isTripleMode) {
      // Triple mode: vGPU spec (cyan) + GPU spec (blue) + vCPU spec (green) + remaining (gray)
      if (isOverLimit) {
        // Over-limit: single merged error stripe bar
        const totalPercentage = vgpuSpecPercentage + gpuSpecPercentage + cpuSpecPercentage
        return (
          <>
            <div
              className="rounded transition-all"
              style={{
                width: `${Math.min(totalPercentage, 100)}%`,
                ...errorStripeStyle,
              }}
            />
            {remainingPercentage > 0 && (
              <div
                className="bg-neutral-200 rounded transition-all"
                style={{ width: `${remainingPercentage}%` }}
              />
            )}
          </>
        )
      }
      // Normal: three colored segments
      return (
        <>
          {vgpuSpecPercentage > 0 && (
            <div
              className="bg-btn-tertiary rounded transition-all"
              style={{ width: `${Math.min(vgpuSpecPercentage, 100)}%` }}
            />
          )}
          {gpuSpecPercentage > 0 && (
            <div
              className="bg-btn-secondary rounded transition-all"
              style={{ width: `${Math.min(gpuSpecPercentage, 100)}%` }}
            />
          )}
          {cpuSpecPercentage > 0 && (
            <div
              className="bg-active rounded transition-all"
              style={{ width: `${Math.min(cpuSpecPercentage, 100)}%` }}
            />
          )}
          {remainingPercentage > 0 && (
            <div
              className="bg-neutral-200 rounded transition-all"
              style={{ width: `${remainingPercentage}%` }}
            />
          )}
        </>
      )
    }

    if (isDualMode) {
      // Dual mode: vGPU (cyan) + GPU Passthrough (blue) + remaining (gray)
      if (isOverLimit) {
        // Over-limit: single merged error stripe bar
        return (
          <>
            <div
              className="rounded transition-all"
              style={{
                width: `${Math.min(vgpuPercentage + gpuPercentage, 100)}%`,
                ...errorStripeStyle,
              }}
            />
            {remainingPercentage > 0 && (
              <div
                className="bg-neutral-200 rounded transition-all"
                style={{ width: `${remainingPercentage}%` }}
              />
            )}
          </>
        )
      }
      // Normal: two colored segments
      return (
        <>
          {vgpuPercentage > 0 && (
            <div
              className="bg-btn-tertiary rounded transition-all"
              style={{ width: `${Math.min(vgpuPercentage, 100)}%` }}
            />
          )}
          {gpuPercentage > 0 && (
            <div
              className="bg-btn-secondary rounded transition-all"
              style={{ width: `${Math.min(gpuPercentage, 100)}%` }}
            />
          )}
          {remainingPercentage > 0 && (
            <div
              className="bg-neutral-200 rounded transition-all"
              style={{ width: `${remainingPercentage}%` }}
            />
          )}
        </>
      )
    }

    // Single value mode: used (green/custom) + remaining (gray)
    return (
      <>
        {percentage > 0 && (
          <div
            className={cn('rounded transition-all', !isOverLimit && color)}
            style={{
              width: `${Math.min(percentage, 100)}%`,
              ...(isOverLimit ? errorStripeStyle : {}),
            }}
          />
        )}
        {percentage < 100 && (
          <div
            className="bg-neutral-200 rounded transition-all"
            style={{ width: `${Math.max(0, 100 - percentage)}%` }}
          />
        )}
      </>
    )
  }

  return (
    <div className="flex-1 min-w-[140px] bg-panel-white border border-neutral-200 rounded-lg px-6 py-4 flex flex-col gap-4">
      <div className="flex items-center justify-between space-x-4">
        <span className="t-bold text-nowrap text-primary">{title}</span>
        {isOverLimit && (
          <div className="text-error bg-error/10 rounded-full px-2 py-0.5 flex items-center space-x-1">
            <AlertTriIcon className="w-4 h-4" />
            <span className="t-sm">Overlimit</span>
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div className="flex gap-1 w-full h-6">{renderProgressBar()}</div>

      {/* Usage text */}
      <div className="flex items-center justify-between flex-wrap gap-x-2">
        <span className={cn('t-xs', isOverLimit ? 'text-danger' : 'text-tertiary')}>
          Using <b className="text-primary">{totalUsed}</b> /{' '}
          <b className="text-primary">{total}</b> {unit}
        </span>
        <span className={cn('t-xs', isOverLimit ? 'text-danger' : 'text-tertiary')}>
          {percentageDisplay}%
        </span>
      </div>
    </div>
  )
})
